import { apiUrls } from "../../../utils/api";
import { translate } from "../../../utils/translate";
import { observer } from "../../../utils/observer";
import { ViewTypes } from "../../../utils/constants";

const notAllowedProps = ["id", "$PhantomId", "tmpid"];

/**
 * @param tasks {[{id: number, [p: string]: any}]}
 * @param saveMask {{text: string}}
 * @param viewType {enum}
 * @returns {Promise<*>}
 */
export const updateTasks = async (tasks = [], saveMask = {}, viewType, apiClient) => {
  const tasksToUpdate = [];
  tasks.forEach((task) => {
    task.updatedProperties = Object.keys(task).filter((i) => !notAllowedProps.includes(i));
    tasksToUpdate.push(task);
  });

  const chunksize = 200;
  const notSavedTaskIds = [];
  for (let i = 0, j = tasksToUpdate.length; i < j; i += chunksize) {
    const nextChunk = i + chunksize;
    let postData = tasksToUpdate.slice(i, nextChunk);
    let updateParams = { tasks: postData, viewType };
    saveMask.text = translate("saveTasks", { total: tasksToUpdate.length, saved: i });
    const resp = await apiClient.post(apiUrls.UpdateMultipleProjectLines, updateParams).catch((err) => {
      console.error("tasks update failed", err.toString(), postData);
    });
    const { cantSaveIDs } = await resp.json();
    notSavedTaskIds.push(...(cantSaveIDs || []));
  }
  observer.trigger("notSavedTasks", notSavedTaskIds);

  return Promise.resolve();
};

function addTask(task, projectID, viewType, apiClient) {
  return apiClient.post(apiUrls.AddProjectLine, {
    task,
    projectID,
    viewType,
  });
}

function removeTask(tasks, projectID, viewType, apiClient) {
  return apiClient.post(apiUrls.DeleteProjectLines, {
    tasks,
    projectID,
    viewType,
  });
}

function removeResourceAssignment(assignment, apiClient) {
  return apiClient.post(apiUrls.RemoveAssignment, {
    ...assignment,
  });
}

export async function correctDependenciesForSplitTask(createdByTaskSplit, apiClient) {
  let aResponse = await apiClient.post(apiUrls.CorrectDependenciesForSplitTask, createdByTaskSplit).catch((err) => {
    console.error("dependency correction failed", err.toString(), postData);
  });
  return Promise.resolve();
}

export async function updateOrAddDependencies(project, dependencies, apiClient) {
  const chunksize = 500;
  for (let i = 0, j = dependencies.length; i < j; i += chunksize) {
    const nextChunk = i + chunksize;
    let postData = dependencies.slice(i, nextChunk);
    let aResponse = await apiClient.post(apiUrls.UpdateOrAddProjectLineBindings, postData).catch((err) => {
      console.error("dependency update failed", err.toString(), postData);
    });
    let ids = await aResponse.json();
    for (let k = 0; k < postData.length; k += 1) {
      const dependencyStore = project.dependencyStore;
      ids.forEach((id, k) => {
        const oldId = postData[k].$PhantomId;
        updateStoreItemId(dependencyStore, oldId, id);
      });
    }
    console.log("done");
  }
  return Promise.resolve();
}

export function removeDependency(dependency, apiClient) {
  return apiClient.post(apiUrls.RemoveProjectLineBinding, {
    ...dependency,
  });
}

function updateStoreItemId(store, oldId, id, parentId) {
  const item = store.getById(oldId);
  if (item) {
    item.beginBatch();
    item.set("id", id, true);
    if (typeof parentId === "string") {
      item.set("parentId", parentId, true);
    }
    item.endBatch();
  }
}

function getJobsCount(entry) {
  let jobsCount = 0;
  if (!entry) return jobsCount;
  if (entry.added) {
    jobsCount++;
  }
  if (entry.updated) {
    jobsCount++;
  }
  if (entry.removed) {
    jobsCount++;
  }
  return jobsCount;
}

function getAssignmentsToUpdate(project) {
  const assignmentsOverride = project.changes.assignments;
  let toBeAdded = assignmentsOverride?.added?.filter((i) => (typeof i.$PhantomId === "string"));
  let toBeUpdated = assignmentsOverride?.added?.filter((i) => (typeof i.$PhantomId === "number") );
  if (toBeUpdated) {
    for (var rec of toBeUpdated) {
      rec.id = rec.$PhantomId;
    }
    if (assignmentsOverride.updated) {
      assignmentsOverride.updated.push(...toBeUpdated);
    } else {
      assignmentsOverride.updated = toBeUpdated;
    }
  }
  
  return {
    ...assignmentsOverride,
    added: toBeAdded?.length ? toBeAdded : undefined,
  };
}

export function saveChanges(project, saveMask = {}, viewType, apiClient) {
  const projectId = project?.originalData?.project?.PK_Project;
  return new Promise(async (resolve) => {
    const updatedTaskId = {};
    let createdByTaskSplit = [];
    if (!project.changes) {
      resolve();
      return;
    }

    //refresh the wbs, the wbs is used by the multi project views
    if (!project.taskStore.isSorted) {
      await project.refreshWbs();
    }

    if (project.taskStore) {
      project.taskStore.beginBatch();
    }

    if (project.assignmentStore) {
      project.assignmentStore.beginBatch();
    }

    if (project.dependencyStore) {
      project.dependencyStore.beginBatch();
    }

    const { tasks, dependencies } = project.changes;
    let assignments = getAssignmentsToUpdate(project);

    const propsToWait = {
      task: getJobsCount(tasks),
      assignments: getJobsCount(assignments),
      dependencies: getJobsCount(dependencies),
    };

    function resolveJob(prop) {
      propsToWait[prop]--;
      const allFinished = !Object.values(propsToWait).find((i) => i !== 0);
      if (allFinished) {
        console.log("all finished", propsToWait);

        if (project.taskStore) {
          project.taskStore.endBatch();
        }

        if (project.assignmentStore) {
          project.assignmentStore.endBatch();
        }

        if (project.dependencyStore) {
          project.dependencyStore.endBatch();
        }

        setTimeout(() => resolve(), 500); //to prevent errors in the departmentview rightmouse context menu (ask Thijs)
      }
    }

    if (tasks?.updated) {
      const updated = tasks.updated.flatMap((i) => {
        if (viewType == ViewTypes.DepartmentsOverView) {
          const t = project.taskStore.getById(i.id);
          if (!t.parentId) { 
            if (i.note || i.taskBarColor) {// allow to update only notes or taskBarColors on root task on department view
              let updatedTask = { id: i.id };
              if (i.note) {
                updatedTask.note = i.note;
              }
              if (i.taskBarColor) {
                updatedTask.taskBarColor = i.taskBarColor;
              }
              return [updatedTask];
            }
            return [];
          }
          if(t.blocked){ //prevent blocked tasks such as appointments from being updated
            return [];
          }
        }
        return [i];
      });
      try {
        await updateTasks(updated, saveMask, viewType, apiClient);
      } finally {
        resolveJob("task");
      }
    }
    if (tasks?.removed) {
      try {
        saveMask.text = translate("Remove tasks") + `(${tasks.removed.length})`;
        await removeTask(tasks.removed, projectId, viewType, apiClient);
      } finally {
        resolveJob("task");
      }
    }
    if (tasks?.added) {
      await new Promise(async (resolve) => {
        for (let index = 0; index < tasks.added.length; index++) {
          const i = tasks.added[index];
          let parentId = i.parentId;
          if (isNaN(Number(i.parentId))) {
            const oldParentId = i.$PhantomParentId;
            parentId = updatedTaskId[oldParentId];
            i.parentId = parentId;
          }
          saveMask.text = translate("createTasks", { total: tasks.added.length, created: index + 1 });
          const stream = await addTask(i, projectId, viewType, apiClient);
          const id = await stream.json();
          const oldId = i.$PhantomId;
          updatedTaskId[oldId] = id;
          let splitFromId = Number(i.SplitFromID);
          if(isNaN(splitFromId)){
            splitFromId = Number(updatedTaskId[i.SplitFromID]);
          }
          createdByTaskSplit.push({splitFromId: splitFromId, splitTaskId : id});
          updateStoreItemId(project.taskStore, oldId, id, parentId);
        }
        await project.taskStore.commitAsync();
        await project.assignmentStore.commitAsync(); 

        assignments = getAssignmentsToUpdate(project);

        resolve();
      }).finally(() => {
        resolveJob("task");
      });
    }

    if (assignments?.added?.length) {
      let added;
      if (viewType == ViewTypes.DepartmentsOverView) {
        const stream = await apiClient.post(apiUrls.UpdateOrAddExplicitAssignments, assignments.added);
        added = await stream.json();
      } else {
        const stream = await apiClient.post(apiUrls.UpdateOrAddAssignments, assignments.added);
        added = await stream.json();
      }
      let changeSet =[];
      assignments?.added.forEach((rec, index) => {
        let chRec = { $PhantomId: rec.$PhantomId, id: added[index] };
        changeSet.push(chRec);
      });
      await project.assignmentStore.applyChangeset(changeSet);

      added?.forEach((id, index) => {
        updateStoreItemId(project.assignmentStore, assignments.added[index].$PhantomId, id);
      });
      resolveJob("assignments");
    }
    if (assignments?.updated) {
      if (viewType == ViewTypes.DepartmentsOverView) {
        await apiClient.post(apiUrls.UpdateOrAddExplicitAssignments, assignments.updated);
      } else {
        await apiClient.post(apiUrls.UpdateOrAddAssignments, assignments.updated);
      }
      
      resolveJob("assignments");
    }

    if (assignments?.removed) {
      Promise.all(assignments.removed.map(element => {
        element.viewType = viewType; 
        return removeResourceAssignment(element, apiClient);
      })).finally(() => {
        resolveJob("assignments");
      });
    }

    if (dependencies?.added) {
      const dependenciesData = dependencies.added.map((i) => {
        i.from = updatedTaskId[i.from] || i.from; // set new task id
        i.to = updatedTaskId[i.to] || i.to; // set new task id
        i.fromTask = i.from;
        i.toTask = i.to;
        //i.type = i.type, type is passed, but it is not needed to do so excplicitly
        viewType = viewType;
        return i;
      });
      saveMask.text = translate("Create dependencies") + `(${dependenciesData.length})`;
      try {
        await updateOrAddDependencies(project, dependenciesData, apiClient);
      } finally {
        resolveJob("dependencies");
      }  
    }
    if (dependencies?.removed || dependencies?.updated) {
      saveMask.text =
        translate("Update/remove dependencies") +
        `(${(dependencies?.removed?.length || 0) + (dependencies?.updated?.length || 0)})`;
    }
    if (dependencies?.removed) {
      Promise.all(dependencies.removed.map(element => {
        element.viewType = viewType; 
        return removeDependency(element, apiClient);
      })).finally(() => {
        resolveJob("dependencies");
      });
    }
    if (project.dependencyStore.changes?.modified?.length > 0) {
      try {
        const dependenciesData = project.dependencyStore.changes.modified.map((i) => {
          i.from = updatedTaskId[i.from] || i.from; // set new task id
          i.to = updatedTaskId[i.to] || i.to; // set new task id
          i.fromTask = i.from;
          i.toTask = i.to;
          //i.type = i.type, type is passed, but it is not needed to do so excplicitly
          viewType = viewType;
          return i;
        });
        await updateOrAddDependencies(project, dependenciesData, apiClient);
      } finally {
        resolveJob("dependencies");
      }
    }

    if(tasks?.added && viewType == ViewTypes.DepartmentsOverView){      
      await correctDependenciesForSplitTask(createdByTaskSplit, apiClient);
    }

    await project.acceptChanges(); //Make sure all changes are cleared after the save
  });
}

export const updateTaskNote = async (taskID, note, api) => {
  api.post(apiUrls.UpdateTaskNote, undefined, { taskID, note });
  return Promise.resolve();
}



